import type {
  GetCredentialsResponse,
  MerchantExternalCredential,
} from '../../../../store/modules/merchant/types';
import {
  colorError,
  colorSuccess,
  colorWarning,
  type StatusColor,
} from '../../../../util/constants';
import { findDefaultCredential } from '../../../../util/credentials';
import { normalizeSquarespaceExternalId } from '../../../../util/squarespaceExternalId';

export const SQUARESPACE_SYSTEM_OF_ORIGIN = 'squarespace';

/** Code Injection in the Squarespace admin, where ecom-connector lands after connect. */
const CODE_INJECTION_PATH = '/config/pages/code-injection';

type SquarespaceCredentialData = {
  externalId?: unknown;
  configuration?: {
    shopUrl?: unknown;
    refreshToken?: unknown;
    refreshTokenExpiresAt?: unknown;
  };
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const credentialData = (credential?: MerchantExternalCredential): SquarespaceCredentialData =>
  (credential?.data ?? {}) as SquarespaceCredentialData;

/** The default ecommerce credential, when it is Squarespace; non-default ones are ignored. */
export const findSquarespaceCredential = (
  credentials: GetCredentialsResponse | undefined,
): MerchantExternalCredential | undefined => {
  const credential = findDefaultCredential(credentials, 'ecommerce');
  return credential?.systemOfOrigin === SQUARESPACE_SYSTEM_OF_ORIGIN ? credential : undefined;
};

export type SquarespaceConnectionStatus = 'not_connected' | 'connected' | 'expired';

/** Refresh tokens expire after 7 idle days, and only reconnecting recovers them. */
export const getSquarespaceConnectionStatus = (
  credential: MerchantExternalCredential | undefined,
  now: number = Date.now(),
): SquarespaceConnectionStatus => {
  const configuration = credentialData(credential).configuration;
  if (!readString(configuration?.refreshToken)) {
    return 'not_connected';
  }

  const expiresAt = Date.parse(readString(configuration?.refreshTokenExpiresAt) ?? '');
  // No recorded expiry: trust the token.
  if (Number.isNaN(expiresAt)) {
    return 'connected';
  }

  return expiresAt > now ? 'connected' : 'expired';
};

export type SquarespaceSite = {
  /** What a merchant recognises their site by. */
  host: string;
  /** Only known for the built-in address, which always serves the admin. */
  codeInjectionUrl?: string;
};

const builtInSite = (subdomain: string): SquarespaceSite => {
  const host = `${subdomain}.squarespace.com`;
  return { host, codeInjectionUrl: `https://${host}${CODE_INJECTION_PATH}` };
};

const hostnameOf = (url: string): string | undefined => {
  try {
    return new URL(url).hostname || undefined;
  } catch {
    return undefined;
  }
};

/** Reads `shopUrl` first, since `externalId` becomes an opaque website id after connect. */
export const getSquarespaceSite = (
  credential: MerchantExternalCredential | undefined,
): SquarespaceSite | undefined => {
  const data = credentialData(credential);
  const shopUrl = readString(data.configuration?.shopUrl);

  if (shopUrl) {
    const subdomain = normalizeSquarespaceExternalId(shopUrl);
    if (subdomain) {
      return builtInSite(subdomain);
    }
    const host = hostnameOf(shopUrl);
    return host ? { host } : undefined;
  }

  const subdomain = normalizeSquarespaceExternalId(readString(data.externalId) ?? '');
  return subdomain ? builtInSite(subdomain) : undefined;
};

/** One link for every merchant; the authorized site picks the merchant. */
export const buildSquarespaceConnectUrl = (
  ecomConnectorBaseUrl: string | undefined,
): string | undefined => {
  const base = readString(ecomConnectorBaseUrl);
  return base ? `${base.replace(/\/+$/u, '')}/v1/webhooks/squarespace/connect` : undefined;
};

const JS_STRING_ESCAPES: Record<string, string> = {
  '\\': '\\\\',
  "'": "\\'",
  // Keeps a value from closing the surrounding <script> tag.
  '<': '\\u003c',
  '\n': '\\n',
  '\r': '\\r',
  '\u2028': '\\u2028',
  '\u2029': '\\u2029',
};

const toJsStringLiteral = (value: string): string =>
  `'${value.replace(/[\\'<\n\r\u2028\u2029]/gu, (char) => JS_STRING_ESCAPES[char])}'`;

export type SquarespaceEllaSnippetOptions = {
  merchantId: string;
  apiBase: string;
  sdkUrl: string;
};

/** Pasted into Code Injection; Squarespace has no API to install it. */
export const buildSquarespaceEllaSnippet = ({
  merchantId,
  apiBase,
  sdkUrl,
}: SquarespaceEllaSnippetOptions): string =>
  [
    '<script>',
    '  (function () {',
    '    var config = {',
    `      apiBase: ${toJsStringLiteral(apiBase)},`,
    `      merchantId: ${toJsStringLiteral(merchantId)},`,
    "      platform: 'squarespace'",
    '    };',
    "    var script = document.createElement('script');",
    `    script.src = ${toJsStringLiteral(sdkUrl)};`,
    '    script.defer = true;',
    '    script.onload = function () {',
    "      if (window.EllaSDK && typeof window.EllaSDK.init === 'function') {",
    '        window.EllaSDK.init(config);',
    '      }',
    '    };',
    '    document.head.appendChild(script);',
    '  })();',
    '</script>',
  ].join('\n');

export type SquarespaceProvisioningStatus = {
  label: string;
  color: StatusColor;
};

export const getSquarespaceProvisioningStatus = (
  connection: SquarespaceConnectionStatus,
  snippetDone: boolean,
): SquarespaceProvisioningStatus => {
  if (connection === 'expired') {
    return { label: 'Connection expired', color: colorError };
  }
  if (connection === 'connected' && snippetDone) {
    return { label: 'Complete', color: colorSuccess };
  }
  return { label: 'Action needed', color: colorWarning };
};
